import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import styles from "./style.module.scss";
import { useNavigate } from "react-router-dom";
import { showAlert } from "@utils/Alert";
import LoadingScreen from "@components/content/LoadingScreen";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { UserApi, UserDataType } from "@/api/UserApi";
import { RoleApi, } from "@/api/RoleApi";
import { ApiClient } from "@/api/ApiClient";

const MAX_USER_AMOUNT = 10

const UserIndex = () => {
  const navigate = useNavigate()
  const modelRef = useRef<ModelIndexHandle>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [roles, setRoles] = useState<Record<string, string>>({})
  const [users, setUsers] = useState<UserDataType[]>([])

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    const roles = await RoleApi.optionsList()
    setRoles(roles)
  }

  /**
   * Cognito va primero por el mismo criterio que UserForm: es la fuente de verdad de la
   * cuenta. Recien despues se borra la fila de DynamoDB; si se hiciera al reves, un fallo
   * de la lambda dejaria una cuenta que puede iniciar sesion pero ya no figura en la lista.
   */
  const deleteRecord = async (id: string) => {
    setIsLoading(true)
    try {
      const userData = await UserApi.get(id);
      if (userData.isOwner) {
        showAlert('danger', 'No se puede eliminar al dueño de la cuenta');
        return;
      }
      await ApiClient.mutations.deleteTenantUserMutation({ username: `${userData.email}` })
      await UserApi.delete(id);
      UserApi.clearEmailLookupCache();
      showAlert('success', 'El Usuario fue eliminado correctamente');
      modelRef.current?.refetch();
    } catch (err) {
      showAlert('danger', (err as Error).message || 'El Usuario no pudo ser eliminado');
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <PageContainer
      title="Lista de Usuarios"
      backUrl="/user/acl"
      primaryActions={[
        {
          onClick: () => {
            if (users.length > MAX_USER_AMOUNT) {
              showAlert("danger", "Usted ya alcanzo el limite de usuario para su perfil, no es posible crear mas de 10 usuario")
            } else {
              navigate('/user/acl/user')
            }
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
    >
      <ModelIndex<UserDataType>
        ref={modelRef}
        fetchData={(...args) => UserApi.fetchAll(...args)}
        // El dueno de la cuenta no consume cupo: su fila la crea el provisioning, no el
        // tenant, y contarla bajaria a 9 el limite efectivo de usuarios creados a mano.
        onDataFetched={(users) => setUsers(users.filter((user) => !user.isOwner))}
        filterProps={[
          {
            key: "name",
            filterType: "contains"
          },
        ]}
        secondaryProperties={[
          {
            key: (data) => roles[data.roleId || ""],
            highlighted: true,
            title: "Rol"
          },
          {
            key: (data) => data.name || "",
            title: "Nombre",
          },
          {
            key: "email",
            title: "Email",
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "2", md: "2" },
            { sm: "3", md: "4" },
            { sm: "3", md: "4" },
          ]
        }
        listActions={[
          {
            href: "/user/acl/user/{:id}",
            actionTitle: "Editar Usuario",
            iconProps: { icon: faEdit }
          },
          {
            actionTitle: "Eliminar Usuario",
            iconProps: { icon: faTrash },
            needConfirm: true,
            confirmProps: {
              title: "Eliminar Usuario",
              confirmMessage: "¿Seguro que desea eliminar este Usuario?",
              confirmBtnText: "Eliminar"
            },
            // El dueno de la cuenta no se puede eliminar: dejaria la suscripcion sin acceso.
            isVisible: (item: UserDataType) => !item.isOwner,
            onClick: (id: string) => { deleteRecord(id) }
          }
        ]}
      >
      </ModelIndex>
      <LoadingScreen isLoading={isLoading} />
    </PageContainer >
  );
}

export default UserIndex;

